import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const theouleSurMerEn: ChauffeurPage = {
  slug: "theoule-sur-mer",
  title: "VTC & private chauffeur in Théoule-sur-Mer",
  subtitle:
    "Private chauffeur in Théoule-sur-Mer: Nice airport transfers, the Estérel massif, red-rock coves and the Corniche d’Or, 7 days a week.",
  keywords: [
    "vtc théoule-sur-mer",
    "private chauffeur theoule",
    "theoule-sur-mer airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Théoule-sur-Mer",
    subtitle:
      "Nice airport transfers, the Estérel red rocks, coves and the Corniche d’Or — available 24/7.",
    keywords: [
      "VTC Théoule",
      "Estérel chauffeur",
      "Nice airport transfer",
      "Corniche d’Or",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Théoule-sur-Mer",
    badges: ["Available 24/7", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Théoule-sur-Mer",
    items: [
      {
        title: "Nice airport, door to door",
        description:
          "Nice airport around 45 minutes away via the A8, with flight tracking and a personal welcome.",
      },
      {
        title: "Estérel roads mastered",
        description:
          "Corniche d’Or, bends and coves: the chauffeur handles the winding road and scarce parking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "French/English, punctual and discreet, at ease on the corniches and in the hinterland.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Théoule-sur-Mer",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up with flight tracking to and from Nice Côte d’Azur airport and Cannes-Mandelieu.",
      },
      {
        title: "Links to Cannes",
        text: "Across the bay, the Croisette and the Palais des Festivals just minutes away.",
      },
      {
        title: "Estérel & Corniche d’Or",
        text: "Panoramic drives between red rocks and sea, from Le Trayas to Saint-Raphaël, to suit the light.",
      },
      {
        title: "Coves, ports & Palais Bulles",
        text: "La Figueirette port, Théoule port, La Galère and the coves, with drop-off as close as possible.",
      },
      {
        title: "Hire & long distance",
        text: "By the hour, the day or any distance: Cannes, Saint-Tropez, Monaco and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Estérel massif",
        description: "Red rocks, Cap Roux and trails facing the sea.",
      },
      {
        name: "Corniche d’Or",
        description:
          "Panoramic coastal road towards Le Trayas and Saint-Raphaël.",
      },
      {
        name: "La Figueirette port",
        description: "Marina at the foot of Miramar.",
      },
      {
        name: "Château de la Théoule",
        description: "Former soap works turned castle, on the seafront.",
      },
      {
        name: "Pointe de l’Aiguille",
        description: "Departmental park, coves and the coastal path.",
      },
      {
        name: "Nice airport",
        description: "About 45 minutes away via the A8 motorway.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur across Théoule-sur-Mer",
    subheading:
      "Between red rocks and sea, premium service on the Estérel roads.",
    paragraphs: [
      "KS Rent Services handles your journeys with a private chauffeur in Théoule-sur-Mer and across the French Riviera: transfers from Nice airport (about 45 minutes), Estérel drives and long-distance trips.",
      "Backed against the Estérel massif, Théoule lines up coves, ports and red rocks along the Corniche d’Or. Our bilingual chauffeurs master the winding road and drop you as close as possible, with no circling for a space.",
      "Across the bay of Cannes, Théoule stays minutes from the Croisette: transfer in the morning, an Estérel walk and dinner in Cannes in the evening — your chauffeur strings the stops together.",
    ],
    bullets: [
      "Flight tracking & personal welcome",
      "Panoramic Corniche d’Or",
      "Cannes & Estérel links",
    ],
  },
  process: {
    heading: "Book a chauffeur in Théoule-sur-Mer",
    steps: [
      "Share your route, timings and number of passengers.",
      "We confirm the right vehicle and the fixed fare.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Live adjustments for traffic and any last-minute changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Théoule-sur-Mer"),
    {
      q: "How long is the transfer to Nice airport from Théoule?",
      a: "Around 45 minutes via the A8 motorway in normal conditions. We run transfers 24/7 with flight tracking and a personal welcome.",
    },
    {
      q: "Is Théoule close to Cannes?",
      a: "Yes, across the bay: we reach the Croisette and the Palais des Festivals within minutes, and Nice airport in about 45 minutes.",
    },
    {
      q: "Do you offer drives in the Estérel and along the Corniche d’Or?",
      a: "Yes, a panoramic route between red rocks and coves, from Le Trayas to Saint-Raphaël, with photo stops possible.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to international guests.",
    },
    {
      q: "Which vehicles do you offer in Théoule-sur-Mer?",
      a: "Mercedes E-Class and S-Class sedans, 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Can you drop us off for a hike in the Estérel?",
      a: "Yes, drop-off at trailheads (Pointe de l’Aiguille, Cap Roux) and pick-up on request, with no parking worry.",
    },
  ],
  relatedLinks: [
    { label: "Cannes chauffeur rates", href: "/en/chauffeur/cannes" },
    { label: "Nice Airport transfers", href: "/en/chauffeur/airport" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Rent a car Cannes", href: "/en/location/car-rental-cannes" },
  ],
  cta: {
    title: "Your private chauffeur in Théoule-sur-Mer",
    text: "Book your transfer or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Théoule-sur-Mer — car service 24/7",
    description:
      "Private car service in Théoule-sur-Mer: Nice airport transfer (~45 min), Estérel massif, Corniche d’Or and coves. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/theoule-sur-mer",
  },
};

export default theouleSurMerEn;
